const ASSET_BASE_URL = import.meta.env.VITE_ASSET_URL;

let webpSupportCache: boolean | null = null;

const cache = new Map<string, Blob>();
const loading = new Map<string, Promise<Blob>>();
const fontCache = new Map<string, FontFace>();

export function useResourceLoader() {
  const resolveAssetUrl = async (path: string): Promise<string> => {
    const format = path.endsWith('.webp') ? 'webp' : path.slice(path.lastIndexOf('.') + 1);
    if (format === 'webp' && !(await checkWebpSupport())) {
      path = path.replace('.webp', '.png');
    }
    if (path.startsWith('http')) {
      return path;
    }
    return `${ASSET_BASE_URL}${path}`;
  };

  const load = async (path: string, onProgress?: (progress: number) => void): Promise<Blob> => {
    const url = await resolveAssetUrl(path);

    if (cache.has(url)) {
      return cache.get(url)!;
    }

    if (loading.has(url)) {
      return loading.get(url)!;
    }

    const loadPromise = fetch(url, {
      mode: 'cors',
      credentials: 'same-origin'
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to load ${url}`);
      }

      const reader = response.body?.getReader();
      const contentLength = +(response.headers.get('Content-Length') ?? '0');

      if (!reader) {
        return response.blob();
      }

      const chunks: Uint8Array[] = [];
      let receivedLength = 0;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        chunks.push(value);
        receivedLength += value.length;

        if (onProgress && contentLength) {
          onProgress((receivedLength / contentLength) * 100);
        }
      }

      const blob = new Blob(chunks);
      cache.set(url, blob);
      loading.delete(url);
      return blob;
    });

    loading.set(url, loadPromise);
    return loadPromise;
  };

  const loadFont = async (fontName: string, fileName: string): Promise<void> => {
    let fontUrl = fileName;
    if (!fileName.startsWith('http')) {
      fontUrl = await resolveAssetUrl(`/font/${fileName}`);
    }

    if (fontCache.has(fontUrl)) {
      return;
    }

    const font = new FontFace(fontName, `url(${fontUrl})`);
    await font.load();
    document.fonts.add(font);
    fontCache.set(fontUrl, font);
  };

  const preload = async (paths: string[]): Promise<Blob[]> => {
    return await Promise.all(paths.map(path => load(path)));
  };

  const loadVideo = (fileName: string, onProgress?: (progress: number) => void) =>
    load(`/video/${fileName}`, onProgress);

  const loadImage = (fileName: string) => load(`/image/${fileName}`);

  const getCachedBlob = async (path: string): Promise<Blob | undefined> => {
    const url = await resolveAssetUrl(path);
    if (cache.has(url)) {
      return cache.get(url)!;
    }
    return undefined;
  };

  const getCachedUrl = async (path: string): Promise<string | undefined> => {
    const url = await resolveAssetUrl(path);
    if (cache.has(url)) {
      return URL.createObjectURL(cache.get(url)!);
    }
    return undefined;
  };

  const loadSequence = async (
    sequenceName: string,
    fileNames: string[],
    specifiedFormat?: string
  ): Promise<void> => {
    const format = specifiedFormat || ((await checkWebpSupport()) ? 'webp' : 'png');
    const maxConcurrency = 60;
    let fallbackToPNG = false;

    const queue = fileNames.map(fileName => `/sequence_frame/${fileName}.${format}`);

    async function handleLoad() {
      while (queue.length > 0) {
        const url = queue.shift();
        const cacheKey = `${sequenceName}_${url!}`;
        if (!cache.has(cacheKey)) {
          try {
            const blob = await load(url!);
            cache.set(cacheKey, blob);
          } catch (error) {
            if (format === 'webp' && !fallbackToPNG && !specifiedFormat) {
              fallbackToPNG = true;
              const pngUrl = url!.replace('.webp', '.png');
              const pngBlob = await load(pngUrl);
              cache.set(`${sequenceName}_${pngUrl}`, pngBlob);
            } else {
              console.error(`Failed to load image: ${url}`, error);
            }
          }
        }
      }
    }
    const loaders = Array.from({ length: maxConcurrency }, () => handleLoad());
    await Promise.all(loaders);
  };

  const checkWebpSupport = (): Promise<boolean> => {
    if (webpSupportCache !== null) {
      return Promise.resolve(webpSupportCache);
    }

    const cachedSupport = localStorage.getItem('webp-support');
    if (cachedSupport !== null) {
      webpSupportCache = cachedSupport === 'true';
      return Promise.resolve(webpSupportCache);
    }

    return new Promise(resolve => {
      const image = new Image();
      image.onload = () => {
        const isSupported = image.width > 0 && image.height > 0;
        webpSupportCache = isSupported;
        localStorage.setItem('webp-support', isSupported.toString());
        resolve(isSupported);
      };
      image.onerror = () => {
        webpSupportCache = false;
        localStorage.setItem('webp-support', 'false');
        resolve(false);
      };
      image.src = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
    });
  };

  return {
    load,
    preload,
    loadFont,
    loadVideo,
    loadImage,
    getCachedBlob,
    getCachedUrl,
    loadSequence
  };
}
